import { useAuth, useUser } from "@tellescope/tanstack-query";
import { DEFAULT_DASHBOARD_CONFIGURATION, useDashboardConfigurationShallowStore } from "../../../store";
import { useTranslation } from "react-i18next";
import { Label, Switch } from "@tellescope/ui";
import { PermissionWrapper } from "../../../permissions";

export interface DashboardSectionProps {
  userId?: string;
}

export const DashboardSection = ({ userId: providedUserId }: DashboardSectionProps) => {
  const { t } = useTranslation("dashboard");
  const { currentUser } = useAuth();
  const providedUser = useUser(providedUserId ?? "", !!providedUserId);
  const user = providedUserId ? providedUser : currentUser;
  const userId = user?.id;
  const { userConfigurations, updateConfiguration } = useDashboardConfigurationShallowStore(
    "userConfigurations",
    "updateConfiguration"
  );
  const currentConfiguration = userConfigurations[userId ?? ""] ?? DEFAULT_DASHBOARD_CONFIGURATION;

  if (!userId) return null;

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">{t("Dashboard Visibility")}</h3>
        <p className="text-sm text-muted-foreground">
          {t("Control which sections are visible on your dashboard. Changes take effect immediately.")}
        </p>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="show-analytics">{t("Activity Analytics")}</Label>
              <p className="text-sm text-muted-foreground">{t("Show analytics and activity metrics on dashboard")}</p>
            </div>
            <Switch
              id="show-analytics"
              checked={currentConfiguration.showAnalytics}
              onCheckedChange={(checked) => updateConfiguration(userId, { showAnalytics: checked })}
            />
          </div>
          <PermissionWrapper model="inbox_threads" action="read">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="show-messages">{t("Messages Section")}</Label>
                <p className="text-sm text-muted-foreground">
                  {t("Display inbox messages and communication activity")}
                </p>
              </div>
              <Switch
                id="show-messages"
                checked={currentConfiguration.showMessages}
                onCheckedChange={(checked) => updateConfiguration(userId, { showMessages: checked })}
              />
            </div>
          </PermissionWrapper>
          <PermissionWrapper model="tickets" action="read">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="show-tickets">{t("Tickets Section")}</Label>
                <p className="text-sm text-muted-foreground">{t("Display ticket status and task management")}</p>
              </div>
              <Switch
                id="show-tickets"
                checked={currentConfiguration.showTickets}
                onCheckedChange={(checked) => updateConfiguration(userId, { showTickets: checked })}
              />
            </div>
          </PermissionWrapper>
          <PermissionWrapper model="call_hold_queues" action="read">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="show-call-hold-queues">{t("Call Hold Queues")}</Label>
                <p className="text-sm text-muted-foreground">{t("Display incoming calls in queues in real-time")}</p>
              </div>
              <Switch
                id="show-call-hold-queues"
                checked={currentConfiguration.showCallHoldQueues}
                onCheckedChange={(checked) => updateConfiguration(userId, { showCallHoldQueues: checked })}
              />
            </div>
          </PermissionWrapper>
          <PermissionWrapper model="ticket_queues" action="read">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="show-ticket-queues">{t("Ticket Queues")}</Label>
                <p className="text-sm text-muted-foreground">{t("Display ticket queues in real-time")}</p>
              </div>
              <Switch
                id="show-ticket-queues"
                checked={currentConfiguration.showTicketQueues}
                onCheckedChange={(checked) => updateConfiguration(userId, { showTicketQueues: checked })}
              />
            </div>
          </PermissionWrapper>
        </div>
      </div>
    </div>
  );
};
